import { createTypeGuard, TypeGuardOf } from "../lib/create-type-guard.js";
import {
  checkCondition,
  Condition,
  ConditionData,
  isCondition,
} from "./condition.js";
import { Keyword } from "./keyword.js";
import { Unit } from "./unit.js";

function sortStatModifiers(a: StatModifier, b: StatModifier): 1 | -1 | 0 {
  // We want to move 'set' operations first,
  if (a.type === b.type) {
    return 0;
  }

  if (a.type === "SET") {
    return -1;
  }

  if (b.type === "SET") {
    return 1;
  }

  return 0;
}

export function applyModifiers(
  unit: Unit,
  modifiers: StatModifier[],
  initialValue: number,
) {
  return modifiers
    .slice()
    .sort(sortStatModifiers)
    .filter(filterModifierByCondition(unit))
    .reduce(reduceModifier, initialValue);
}

function filterModifierByCondition(unit: Unit) {
  return (modifier: StatModifier) => {
    if (!modifier.conditions) {
      // If no conditions are specified we always accept that modifier
      return true;
    }

    return modifier.conditions.some(checkCondition(modifier.keyword.id, unit));
  };
}

function reduceModifier(accumulator: number, effect: StatModifier) {
  switch (effect.type) {
    case "INCREMENT": {
      return accumulator + effect.value;
    }

    case "MULTIPLY": {
      return Math.floor(accumulator * effect.value);
    }

    case "SET": {
      return effect.value;
    }

    // Unknown type is a noop.
    default: {
      return accumulator;
    }
  }
}

export type StatModifierData = {
  type: string;
  stat: string;
  value: number;
  conditions?: ConditionData[];
};

export type StatModifierType = TypeGuardOf<typeof isStatModifierType>;

const isStatModifierType = createTypeGuard(["INCREMENT", "MULTIPLY", "SET"]);

export type StatModifierTargetStat = TypeGuardOf<
  typeof isStatModifierTargetStat
>;

const isStatModifierTargetStat = createTypeGuard([
  "movement",
  "combat",
  "defense",
  "morale",
  "range",
]);

export class StatModifier {
  keyword: Keyword;
  type: StatModifierType;
  stat: StatModifierTargetStat;
  value: number;
  conditions: Condition[] | null;

  constructor(
    keyword: Keyword,
    type: StatModifierType,
    stat: StatModifierTargetStat,
    value: number,
    conditions?: ConditionData[],
  ) {
    this.keyword = keyword;
    this.type = type;
    this.stat = stat;
    this.value = value;
    this.conditions = conditions?.filter(isCondition) ?? null;
  }

  static from(keyword: Keyword, data: StatModifierData) {
    if (isStatModifierType(data.type) && isStatModifierTargetStat(data.stat)) {
      return new StatModifier(
        keyword,
        data.type,
        data.stat,
        data.value,
        data.conditions,
      );
    }

    return null;
  }
}
